import type { FhssRuleSet } from "./types.js";

// FHSS ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// Every figure is transcribed from the FHSS verification ledgers
// (docs/research/discovery/verification/fhss-eligibility-releasable-checker-figures.md
//  + fhss-benefit-estimator-figures.md) with its source + the per-figure stamp.
// All calculator-driving figures (the $15k/$50k caps, the gates, the 85%/100%
// split, the marginal−30% release offset, the 15% contributions tax) are CITED.
//
// Never edit a shipped ruleset to "fix" a later year — author a new fyXXXX-XX.ts
// and register it. Historical reclaims resolve against the rules in force then
// (e.g. before 1 Jul 2022 the lifetime cap was $30k — a separate FY ruleset).
export const fy2025_26: FhssRuleSet = {
  engine: "fhss",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    fhss:
      "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/withdrawing-and-using-your-super/early-access-to-super/first-home-super-saver-scheme",
    firstHomeBuyers: "https://firsthomebuyers.gov.au/first-home-super-saver-scheme",
    sicRate:
      "https://www.ato.gov.au/tax-rates-and-codes/shortfall-interest-charge-rates",
    contributionsTax:
      "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/caps-limits-and-tax-on-super-contributions",
  },
  sourcesVerifiedAt: "2026-06-22",

  // Caps — ledger figs 1 & 2, both CITED ("$15,000 in any one financial year",
  // "$50,000 across all years"). The lifetime $50k is current (raised from $30k
  // on 1 Jul 2022 — pre-2022 reclaims resolve against a separate FY ruleset).
  annualEligibleCap: 15_000,
  lifetimeEligibleCap: 50_000,

  // Release split — ledger fig 4/5, CITED verbatim: "only 85% of concessional
  // contributions ... 100% of non-concessional contributions count towards the
  // release amount." Worked example: 85% of $8,400 = $7,140.
  concessionalReleaseRate: 0.85,
  nonConcessionalReleaseRate: 1.0,

  // Contributions tax in — benefit ledger fig 4, CITED: "Concessional
  // contributions are taxed at 15% for most individuals" (Div 293 lifts to 30%
  // above $250k income, out of the 28–45 cohort).
  concessionalContributionsTaxRate: 0.15,

  // Release tax out — benefit ledger fig 3, CITED verbatim: assessable amount
  // "taxed at your marginal tax rate less a 30% tax offset"; the marginal slice
  // is Medicare-inclusive. Default 17% withholding when the ATO can't estimate.
  releaseTaxOffset: 0.3,
  defaultWithholdingRate: 0.17,

  // Eligibility gates — ledger fig 3, CITED: "18 or older when requesting a FHSS
  // determination"; never owned AU property (hardship-exception branch);
  // not previously released.
  minAge: 18,

  // Timing — ledger fig 5, CITED: sign a contract within 12 months of the
  // release request; ATO-extendable by a further 12 (max 24). A note, not calc.
  contractWindowMonths: 12,
  contractWindowMaxMonths: 24,
};
